/**
 * Client.ts — Cliente WebSocket do servidor de jogo Node.
 *
 * Resolve URL conforme ambiente:
 *   localhost      → ws://localhost:4100/?token=X
 *   games.*        → wss://games.ensinosocial.com.br/spacewar/ws/?token=X
 *
 * Heartbeat ping a cada 5s. Dispatch via callbacks tipados.
 * Refator de tres.php:1987-2067.
 */

import type {
  MsgServidor,
  MsgAuthOk,
  MsgAuthErro,
  MsgPartidaInicio,
  MsgEstado,
  MsgFrotaResposta,
  MsgTorreResposta,
  MsgPartidaFim,
  MsgCliente,
} from './protocolo';

export interface WSHandlers {
  onAbrir?: (url: string) => void;
  onAuthOk?: (msg: MsgAuthOk) => void;
  onAuthErro?: (msg: MsgAuthErro) => void;
  onPartidaInicio?: (msg: MsgPartidaInicio) => void;
  onEstado?: (msg: MsgEstado) => void;
  onFrotaResposta?: (msg: MsgFrotaResposta) => void;
  onTorreResposta?: (msg: MsgTorreResposta) => void;
  onPartidaFim?: (msg: MsgPartidaFim) => void;
  onFechar?: (code: number) => void;
  onErro?: () => void;
}

export class WSClient {
  private ws: WebSocket | null = null;
  private heartbeat: number | null = null;
  private conectouCedo = false;

  constructor(private readonly handlers: WSHandlers) {}

  conectar(token: string): void {
    const url = construirUrl(token);
    try {
      this.ws = new WebSocket(url);
    } catch (e) {
      if (this.handlers.onErro) this.handlers.onErro();
      console.error('[WSClient] Falha ao criar WebSocket:', e);
      return;
    }

    this.ws.addEventListener('open', () => {
      this.conectouCedo = true;
      if (this.handlers.onAbrir) this.handlers.onAbrir(url);
    });

    this.ws.addEventListener('message', (ev) => this.onMessage(ev));

    this.ws.addEventListener('close', (ev) => {
      this.pararHeartbeat();
      if (!this.conectouCedo && this.handlers.onErro) {
        this.handlers.onErro();
      }
      if (this.handlers.onFechar) this.handlers.onFechar(ev.code);
    });

    this.ws.addEventListener('error', () => {
      if (this.handlers.onErro) this.handlers.onErro();
    });
  }

  enviar(msg: MsgCliente): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(msg));
  }

  desconectar(): void {
    this.pararHeartbeat();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  private onMessage(ev: MessageEvent): void {
    let msg: MsgServidor;
    try {
      msg = JSON.parse(ev.data) as MsgServidor;
    } catch {
      return;
    }

    switch (msg.tipo) {
      case 'auth_ok':
        this.iniciarHeartbeat();
        this.handlers.onAuthOk?.(msg);
        break;
      case 'auth_erro':
        this.handlers.onAuthErro?.(msg);
        break;
      case 'partida_inicio':
        this.handlers.onPartidaInicio?.(msg);
        break;
      case 'estado':
        this.handlers.onEstado?.(msg);
        break;
      case 'frota_resposta':
        this.handlers.onFrotaResposta?.(msg);
        break;
      case 'torre_resposta':
        this.handlers.onTorreResposta?.(msg);
        break;
      case 'partida_fim':
        this.handlers.onPartidaFim?.(msg);
        break;
      case 'pong':
        // noop
        break;
    }
  }

  private iniciarHeartbeat(): void {
    this.heartbeat = window.setInterval(() => {
      this.enviar({ tipo: 'ping' });
    }, 5000);
  }

  private pararHeartbeat(): void {
    if (this.heartbeat !== null) {
      clearInterval(this.heartbeat);
      this.heartbeat = null;
    }
  }
}

// ---------------------------------------------------------------------------

function construirUrl(token: string): string {
  const host = window.location.hostname;
  const isLocal =
    host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.');

  let qs = `?token=${encodeURIComponent(token)}`;
  const paramsUrl = new URLSearchParams(window.location.search);
  const bot = paramsUrl.get('bot');
  if (bot) qs += `&bot=${encodeURIComponent(bot)}`;

  // Local: container games_spacewar exposto em :4101 (compose em GAMES/spacewar-web/).
  // O `ensino_jogo` legado ainda escuta :4100 ate ser desativado (Fase 7).
  if (isLocal) return `ws://${host}:4101/${qs}`;
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${host}/spacewar/ws/${qs}`;
}
